<template>
  <main class="plant-visit-details" data-cy="plant-visit-details-view">
    <section v-if="state === 'loading'" class="plant-visit-details__state">
      <Loader size="large" type="pulsing" data-cy="plant-visit-details-loader" />
      <p>Loading visit details…</p>
    </section>

    <section v-else-if="state === 'invalid'" class="plant-visit-details__state">
      <h1>Invalid visit link</h1>
      <p>The email link does not contain a valid visit ID.</p>
    </section>

    <section v-else-if="state === 'not-found'" class="plant-visit-details__state">
      <h1>Visit not found</h1>
      <p>The requested visit could not be found or is no longer available.</p>
    </section>

    <section v-else-if="state === 'error'" class="plant-visit-details__state">
      <h1>Unable to load visit</h1>
      <p>There was a problem loading this visit. Please try again later.</p>
    </section>

    <template v-else-if="details">
      <header class="plant-visit-details__header">
        <div>
          <p class="eyebrow">Plant RFR Assignment</p>
          <h1>{{ details.PlantName || "Plant Details" }}</h1>
          <p v-if="details.CompanyName" class="subtitle">{{ details.CompanyName }}</p>
        </div>
        <div class="visit-id">Visit ID {{ details.VisitId }}</div>
      </header>

      <section class="plant-visit-details__content">
        <div class="details-card">
          <h2>Plant Details</h2>
          <dl class="details-grid">
            <div>
              <dt>Plant</dt>
              <dd>{{ details.PlantName || "—" }}</dd>
            </div>
            <div>
              <dt>Address</dt>
              <dd>{{ details.PlantAddress || "—" }}</dd>
            </div>
            <div>
              <dt>Company</dt>
              <dd>
                {{ details.CompanyName || "—" }}
                <span v-if="details.CompanyId" class="muted">({{ details.CompanyId }})</span>
              </dd>
            </div>
            <div>
              <dt>Assignment Type</dt>
              <dd>{{ visitTypeName }}</dd>
            </div>
            <div>
              <dt>Assignment Date Range</dt>
              <dd>{{ periodDisplay }}</dd>
            </div>
           <!-- <div>
              <dt>Actual visit date</dt>
              <dd>{{ formatDate(details.ActualVisitDate) }}</dd>
            </div> -->
            <div>
              <dt>RFR</dt>
              <dd>{{ details.RfrName || "—" }}</dd>
            </div>
            <div>
              <dt>RC</dt>
              <dd>{{ details.Rc || "—" }}</dd>
            </div>
          </dl>
          <div class="contact-grid">
            <section>
              <h3>Plant Primary Contact</h3>
              <dl class="contact-details">
                <div>
                  <dt>Name</dt>
                  <dd>{{ details.PlantContactName || "—" }}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{{ details.PlantContactPhone || "—" }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ details.PlantContactEmail || "—" }}</dd>
                </div>
              </dl>
            </section>
            <section v-if="isInitialVisitType">
              <h3>Plant Setup RC</h3>
              <dl class="contact-details">
                <div>
                  <dt>Name</dt>
                  <dd>{{ details.SetupRcName || "—" }}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{{ details.SetupRcPhone || "—" }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ details.SetupRcEmail || "—" }}</dd>
                </div>
              </dl>
            </section>
          </div>
        </div>

        <PlannedVisitDateEditor
          :visit-id="details.VisitId"
          :planned-visit-date="details.PlannedVisitDate"
          :can-edit="details.CanEditPlannedVisitDate"
          @date-updated="onDateUpdated"
        />
      </section>

      <nav class="plant-visit-details__links" aria-label="Related visit links">
        <a :href="plantHistoryUrl" target="_blank" rel="noopener noreferrer">
          <font-awesome-icon icon="history" aria-hidden="true" />
          Plant history
        </a>
        <a :href="visitFormUrl" target="_blank" rel="noopener noreferrer">
          <font-awesome-icon icon="file-alt" aria-hidden="true" />
          Open inspection form
        </a>
      </nav>
    </template>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRef } from "vue";
import { Loader } from "@progress/kendo-vue-indicators";
import { api } from "@/api";
import type { PlantVisitDetailsDisplay } from "@/api/plantVisitDetails";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import enumHelper from "@/shared/helpers/enumHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { Pages } from "@/pages/pages";
import PlannedVisitDateEditor from "./PlannedVisitDateEditor.vue";

type ViewState = "loading" | "invalid" | "not-found" | "error" | "loaded";

const props = defineProps<{
  requestedVisitId?: number;
}>();

const state = ref<ViewState>("loading");
const details = ref<PlantVisitDetailsDisplay | null>(null);
const visitId = toRef(props, "requestedVisitId");

const formatDate = (value: string | null | undefined) => (value ? new Date(value).toLocaleDateString() : "—");

const visitTypeName = computed(() => {
  if (!details.value) return "—";
  return enumHelper.splitByCapitalLetter(EnumVisitType[details.value.VisitType]);
});

const isInitialVisitType = computed(() => details.value?.VisitType === EnumVisitType.Initial);

const periodDisplay = computed(() => {
  if (!details.value) return "—";
  const start = formatDate(details.value.PeriodStartDate);
  const end = formatDate(details.value.PeriodEndDate);
  return start === "—" && end === "—" ? "—" : `${start} – ${end}`;
});

const plantHistoryUrl = computed(() =>
  details.value ? Pages.PlantHistory.replace(":plantId", details.value.PlantId.toString()) : "#"
);

const visitFormUrl = computed(() =>
  details.value
    ? `${Pages.VisitInspectionForm}?category=Inspection&mode=view&visitID=${details.value.VisitId}&menu=VisitsAsRfr`
    : "#"
);

const onDateUpdated = (plannedDate: string | null) => {
  if (details.value) details.value.PlannedVisitDate = plannedDate;
};

const loadDetailsAsync = async () => {
  const requestedVisitId = visitId.value;
  if (!requestedVisitId) {
    state.value = "invalid";
    return;
  }

  await handleResponsePromiseAsync(
    api.get("GetPlantVisitDetails", { visitId: requestedVisitId }),
    (data) => {
      if (!data) {
        state.value = "not-found";
        return;
      }
      details.value = data;
      state.value = "loaded";
    },
    "PlantVisitDetailsView.vue",
    "loadDetailsAsync",
    () => {
      state.value = "error";
    }
  );

  if (state.value === "loading") state.value = "error";
};

onMounted(loadDetailsAsync);
</script>

<style scoped lang="scss">
.plant-visit-details {
  width: min(100% - 32px, 1080px);
  margin: 0 auto;
  padding: 32px 0 48px;
  color: #1e293b;

  &__state {
    display: flex;
    min-height: 420px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 28px;
    color: #ffffff;
    background: #244880;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgb(30 64 110 / 18%);
  }

  .eyebrow {
    margin: 0 0 6px;
    color: #dbeafe;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.8;
  }

  h1,
  h2,
  p {
    margin-top: 0;
  }

  h1 {
    margin-bottom: 6px;
    font-size: clamp(1.5rem, 3vw, 2.1rem);
  }

  .subtitle {
    margin-bottom: 0;
    color: #dbeafe;
  }

  .visit-id {
    flex: 0 0 auto;
    padding: 8px 12px;
    color: #dbeafe;
    font-size: 0.9rem;
    font-weight: 700;
    background: rgb(255 255 255 / 15%);
    border-radius: 999px;
  }

  &__content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
    gap: 24px;
    margin-top: 24px;
  }

  .details-card {
    padding: 24px;
    background: #fff;
    border: 1px solid #dbe4f0;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgb(15 23 42 / 6%);
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px 24px;
    margin: 0;
  }

  .contact-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;

    h3 {
      margin-bottom: 14px;
      color: #334155;
      font-size: 0.95rem;
    }
  }

  .contact-details {
    display: grid;
    gap: 14px;
    margin: 0;
  }

  dt {
    margin-bottom: 5px;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .muted {
    color: #64748b;
  }

  &__links {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 24px;

    a {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #245a99;
      font-weight: 600;
      text-decoration: none;
    }

    a:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 700px) {
    width: min(100% - 20px, 1080px);
    padding-top: 20px;

    &__header {
      flex-direction: column;
      padding: 22px;
    }

    &__content {
      grid-template-columns: 1fr;
    }

    .details-grid {
      grid-template-columns: 1fr;
    }

    .contact-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
